import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Send, Mic, X, Square, Pause, Play, BookOpen, Layers, Sparkles, Eye, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { createChatSession, sendChatMessage, sendChatMessageStream, getOrCreateSession, getChatSession, updateSessionExplanationMode, cancelChatGeneration } from '@/api/chat';
import { useSubjectChapter } from '@/context/SubjectChapterContext';

import BudleeAIIcon from '@/components/BudleeAIIcon';
import StatsBar from '@/components/StatsBar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import MermaidRenderer from '@/components/MermaidRenderer';
import InteractiveQuiz from '@/components/InteractiveQuiz';
import SimulationRenderer from '@/components/simulations/SimulationRenderer';

import { sanitizeChatMarkdown } from '@/utils/chatMarkdownSanitizer';
import { makeStreamingMarkdownSafe } from '@/utils/streamingMarkdown';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import { coerceToPlainText } from '@/utils/coerceToPlainText';

// Use the exact same sanitizer as the regular Chat page so LaTeX repair
// (e.g. `rac{` -> `\\frac{`, `imes` -> `\\times`) stays consistent.
const formatAnswerForDisplay = (content, { streaming = false } = {}) => {
  const sanitized = sanitizeChatMarkdown(content, { streaming });
  return streaming ? makeStreamingMarkdownSafe(sanitized) : sanitized;
};

const isCancelError = (e) => {
  return (
    e?.code === 'CANCELLED' ||
    e?.name === 'AbortError' ||
    e?.code === 'ERR_CANCELED' ||
    e?.name === 'CanceledError'
  );
};
// Helper: auto-wrap raw Mermaid source in code block if not already
function autoWrapMermaid(content) {
  if (typeof content !== 'string') return content;
  // Detect common Mermaid diagram patterns not in code block
  // e.g. "mermaid flowchart TD" or "mermaid\nflowchart TD"
  const mermaidPattern = /(^|\n)\s*mermaid\s+flowchart\s+TD[\s\S]*?(?=\n\s*\n|$)/gi;
  let replaced = content;
  replaced = replaced.replace(mermaidPattern, (match) => {
    // If already in a code block, skip
    if (/```mermaid[\s\S]*?```/i.test(match)) return match;
    // Otherwise, wrap in code block
    return `\n\n\u0060\u0060\u0060mermaid\n${match.trim().replace(/^mermaid\s*/i, '')}\n\u0060\u0060\u0060\n`;
  });
  return replaced;
}

const normalizeFigureRef = (value) => {
  const raw = String(value || '').trim();
  // Unify Fig/Figs/Figure/Figures + strip minor punctuation so keys match.
  const match = raw.match(/\b(?:figs?\.?|figures?|figure)\s*(\d+(?:[.\-]\d+)?)\b/i);
  if (match && match[1]) return `figure ${match[1]}`;
  return raw
    .toLowerCase()
    .replace(/\bfigs\.?\b/g, 'figure')
    .replace(/\bfig\.?\b/g, 'figure')
    .replace(/\bfigures\b/g, 'figure')
    .replace(/\s+/g, ' ')
    .replace(/\s*:\s*$/, '')
    .trim();
};

// Extract interactive quiz payloads so we don't show raw JSON in the chat.
// Expected format:
//   Interactive Activity\n{...json...}
const parseInteractiveContent = (content) => {
  const safe = typeof content === 'string' ? content : String(content ?? '');
  // Matches: new line + (optional md prefixes) + Interactive Activity + (newline/end/colon)
  const headerRegex = /(?:^|[\n\r])(?:[\t >#*\-`]*)(?:Interactive Activity)(?:[\t >#*\-`]*)(?:\n|$|:)/im;
  const match = safe.match(headerRegex);
  if (!match) return { cleanContent: safe, quizData: null, simulation: null };

  const cleanContent = safe.substring(0, match.index).trim();
  const tail = safe.substring(match.index);
  let quizData = null;
  let simulation = null;

  const firstBrace = tail.indexOf('{');
  if (firstBrace !== -1) {
    const lastBrace = tail.lastIndexOf('}');
    if (lastBrace > firstBrace) {
      try {
        const jsonCand = tail.substring(firstBrace, lastBrace + 1);
        const parsed = JSON.parse(jsonCand);
        if (parsed) {
          if (parsed.simulation_type || parsed.simulationType) {
            const simulationType = parsed.simulation_type || parsed.simulationType;
            const simulationData = parsed.simulation_data || parsed.simulationData;
            if (simulationType && simulationData) {
              simulation = { simulationType, simulationData, title: parsed.title, description: parsed.description };
            }
          } else if (Array.isArray(parsed.parts)) {
            simulation = { simulationType: 'interactive_diagram', simulationData: parsed };
          } else if (parsed.type || parsed.question) {
            quizData = parsed;
          }
        }
      } catch {
        const codeBlockMatch = tail.match(/```json\s*([\s\S]*?)\s*```/);
        if (codeBlockMatch && codeBlockMatch[1]) {
          try {
            const parsed = JSON.parse(codeBlockMatch[1]);
            if (parsed) {
              if (parsed.simulation_type || parsed.simulationType) {
                const simulationType = parsed.simulation_type || parsed.simulationType;
                const simulationData = parsed.simulation_data || parsed.simulationData;
                if (simulationType && simulationData) {
                  simulation = { simulationType, simulationData, title: parsed.title, description: parsed.description };
                }
              } else if (Array.isArray(parsed.parts)) {
                simulation = { simulationType: 'interactive_diagram', simulationData: parsed };
              } else {
                quizData = parsed;
              }
            }
          } catch { }
        }
      }
    }
  }

  return { cleanContent, quizData, simulation };
};

const AnswerMarkdown = ({
  children,
  className,
  messageId,
  citationSourceIds = [],
  sourceById = null,
  onCitationClick = null,
  figureRefMap = null,
  streaming = false,
}) => {
  const [renderKey, setRenderKey] = useState(0);
  const [katexReady, setKatexReady] = useState(false);

  // Match ChatPage behavior: if KaTeX CSS loads after first render,
  // force a rerender so equations are laid out correctly.
  useEffect(() => {
    const checkKatexReady = () => {
      const katexStyles =
        document.querySelector('link[href*="katex"]') ||
        document.querySelector('style[data-emotion*="katex"]');

      if (katexStyles) {
        setKatexReady(true);
        return true;
      }
      return false;
    };

    if (checkKatexReady()) {
      setRenderKey((prev) => prev + 1);
      return;
    }

    const timer = setInterval(() => {
      if (checkKatexReady()) {
        clearInterval(timer);
        setRenderKey((prev) => prev + 1);
      }
    }, 100);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (katexReady) setRenderKey((prev) => prev + 1);
  }, [messageId, katexReady]);

  // Apply auto-wrap Mermaid diagrams. `children` is assumed to have already
  // been sanitized by formatAnswerForDisplay before hitting AnswerMarkdown.
  let processed = autoWrapMermaid(children);
  if (streaming) processed = makeStreamingMarkdownSafe(processed);

  const propsRef = useRef({ onCitationClick, figureRefMap, citationSourceIds, sourceById, messageId });
  propsRef.current = { onCitationClick, figureRefMap, citationSourceIds, sourceById, messageId };

  const memoizedComponents = useMemo(() => {
    // Helper to process text and Replace "Figure X" with clickable spans
    const replaceSuperscripts = (text) => {
      if (!text) return null;
      const parts = text.split(/([¹²³⁴⁵⁶⁷⁸⁹⁰]+)/g);
      const processedParts = Array.isArray(parts) ? parts : [text];

      return processedParts.map((part, i) => {
        if (typeof part !== 'string') return part;

        const figureRegex = /((?:Figures?|Figs?\.?)\s+\d+(?:[.\-]\d+)?)/g;
        const subParts = part.split(figureRegex);
        if (subParts.length === 1) return part;

        return subParts.map((subPart, j) => {
          if (subPart.match(/^(?:Figures?|Figs?\.?)\s+\d+(?:[.\-]\d+)?$/)) {
            const normalized = normalizeFigureRef(subPart);
            const pageForFigure = propsRef.current.figureRefMap instanceof Map ? propsRef.current.figureRefMap.get(normalized) : null;
            return (
              <span
                key={`${i}-${j}`}
                className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded-md bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-xs font-semibold cursor-pointer hover:bg-[hsl(var(--primary))]/20 transition-colors select-none border border-[hsl(var(--primary))]/20"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (propsRef.current.onCitationClick) {
                    propsRef.current.onCitationClick({
                      figure: subPart,
                      page: pageForFigure || null
                    });
                  }
                }}
                title="View Figure in Textbook"
              >
                <Eye className="w-3 h-3" />
                {subPart}
              </span>
            );
          }
          return subPart;
        });
      }).flat();
    };

    const processChildren = (nodeChildren) => {
      return React.Children.map(nodeChildren, child => {
        if (typeof child === 'string') {
          return replaceSuperscripts(child);
        }
        if (React.isValidElement(child) && child.props.children) {
          return React.cloneElement(child, {
            children: processChildren(child.props.children)
          });
        }
        return child;
      });
    };

    const renderCiteMarkers = () => {
      const citedIds = Array.isArray(propsRef.current.citationSourceIds) ? propsRef.current.citationSourceIds.filter(Boolean) : [];
      if (!citedIds.length || !propsRef.current.sourceById || typeof propsRef.current.onCitationClick !== 'function') return null;
      return (
        <span className="ml-1 inline-flex gap-1 align-super">
          {citedIds.map((sid) => {
            const src = propsRef.current.sourceById.get(sid);
            if (!src) return null;
            return (
              <button
                key={`cite-${propsRef.current.messageId}-${sid}`}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  propsRef.current.onCitationClick(src);
                }}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-[10px] font-semibold leading-none"
                title={`Jump to page ${src.page}`}
              >
                s
              </button>
            );
          })}
        </span>
      );
    };

    return {
      p: ({ children: pChildren, ...props }) => (
        <p {...props}>
          {processChildren(pChildren)}
          {renderCiteMarkers()}
        </p>
      ),
      li: ({ children: liChildren, ...props }) => (
        <li {...props}>
          {processChildren(liChildren)}
          {renderCiteMarkers()}
        </li>
      ),
      pre: ({ className: preClassName, ...props }) => (
        <pre
          {...props}
          className={[
            preClassName,
            'max-w-full overflow-x-hidden whitespace-pre-wrap break-words'
          ].filter(Boolean).join(' ')}
          style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        />
      ),
      code: ({ inline, className: codeClassName, children: codeChildren, ...props }) => {
        const match = /language-(\w+)/.exec(codeClassName || '');
        const lang = match?.[1]?.toLowerCase?.();

        if (!inline && lang === 'mermaid') {
          console.log('✅ [BudleeAIChatPanel] Detected mermaid code block, className:', codeClassName, 'lang:', lang);
        } else if (!inline && codeClassName) {
          console.log('🔍 [BudleeAIChatPanel] Code block detected - className:', codeClassName, 'lang:', lang);
        }

        if (!inline && lang === 'mermaid') {
          const chart = String(codeChildren || '').replace(/\n$/, '');
          return (
            <div className="my-4">
              <MermaidRenderer chart={chart} />
            </div>
          );
        }

        return (
          <code
            {...props}
            className={[
              codeClassName,
              'max-w-full whitespace-pre-wrap break-words'
            ].filter(Boolean).join(' ')}
            style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            data-inline={inline ? 'true' : 'false'}
          >
            {codeChildren}
          </code>
        );
      },
    };
  }, []);

  return (
    <div
      className={[
        'min-w-0 max-w-full break-words text-sm leading-relaxed text-foreground',
        // Wrapping / overflow guards
        "[&_pre]:max-w-full [&_pre]:overflow-x-hidden [&_pre]:whitespace-pre-wrap [&_pre]:break-words",
        "[&_pre_code]:whitespace-pre-wrap [&_pre_code]:break-words",
        "[&_code]:max-w-full [&_code]:break-words [&_code]:whitespace-pre-wrap",
        "[&_a]:break-words",
        "[&_p]:my-3 [&_p]:leading-7",
        "[&_ul]:my-3 [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:list-disc [&_ul]:list-outside",
        "[&_ol]:my-3 [&_ol]:pl-5 [&_ol]:space-y-1 [&_ol]:list-decimal [&_ol]:list-outside",
        "[&_li::marker]:text-muted-foreground",
        // Headings: use subtle dividers so structured answers read like a study note.
        "[&_h2]:mt-6 [&_h2]:mb-2.5 [&_h2]:text-base [&_h2]:font-bold [&_h2]:pb-1 [&_h2]:border-b [&_h2]:border-[hsl(var(--primary))]/25",
        "[&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:pb-0.5 [&_h3]:border-b [&_h3]:border-[hsl(var(--card-border))]",
        "[&_h4]:mt-3 [&_h4]:mb-1.5 [&_h4]:text-sm [&_h4]:font-semibold",
        "[&_strong]:font-semibold [&_strong]:text-foreground",

        // Callouts / quotes (useful for 'Exam tip', 'Common mistakes', etc.)
        "[&_blockquote]:my-4 [&_blockquote]:rounded-xl [&_blockquote]:border [&_blockquote]:border-[hsl(var(--card-border))]",
        "[&_blockquote]:bg-[hsl(var(--card-bg))] [&_blockquote]:px-4 [&_blockquote]:py-3",
        "[&_blockquote]:border-l-4 [&_blockquote]:border-l-[hsl(var(--primary))]/40",
        "[&_blockquote_p]:my-1 [&_blockquote_p]:leading-7",

        "[&_hr]:my-5 [&_hr]:border-[hsl(var(--card-border))]",

        // Tables (match main chat styling)
        "[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse",
        "[&_table]:border [&_table]:border-[hsl(var(--card-border))]",
        "[&_th]:border [&_th]:border-[hsl(var(--card-border))] [&_th]:bg-[hsl(var(--card-bg))] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground",
        "[&_td]:border [&_td]:border-[hsl(var(--card-border))] [&_td]:px-3 [&_td]:py-2 [&_td]:align-top",

        "[&_.katex-display]:block [&_.katex-display]:w-full [&_.katex-display]:max-w-none [&_.katex-display]:text-center",
        "[&_.katex-display]:my-6 [&_.katex-display]:py-4 [&_.katex-display]:px-5 [&_.katex-display]:rounded-xl",
        "[&_.katex-display]:overflow-x-auto",
        "[&_.katex-display]:bg-[hsl(var(--card-bg))] [&_.katex-display]:border [&_.katex-display]:border-[hsl(var(--card-border))]",
        "[&_.frac-line]:border-b-[1.5px] [&_.frac-line]:border-current",
        className,
      ].join(' ')}
    >
      <ReactMarkdown
        remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
        rehypePlugins={[[rehypeKatex, { throwOnError: false, output: 'html', strict: false, trust: true }]]}
        components={memoizedComponents}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
};

// Mock images for demo
const mockImages = [
  'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=200&h=150&fit=crop',
  'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=200&h=150&fit=crop',
  'https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?w=200&h=150&fit=crop'
];

const mockVideos = [
  { title: 'Introduction to Force', duration: '3:45' },
  { title: 'Newton\'s Laws Explained', duration: '5:20' }
];

const CitedAnswer = ({ answer, messageId, figureRefMap = null, onCitationClick = null, streaming = false }) => {
  // Sources/citations UI is a placeholder for later; keep answers clean for now.
  return (
    <AnswerMarkdown messageId={messageId} figureRefMap={figureRefMap} onCitationClick={onCitationClick} streaming={streaming}>
      {answer}
    </AnswerMarkdown>
  );
};

// Global cache to persist messages and tab modes across page navigation within the same session
const globalSessionCache = new Map();

export default function BudleeAIChatPanel({
  totalXP = null,
  onClose,
  prefilledText = '',
  onClearPrefilledText,
  prefilledAnchor = null,
  onClearPrefilledAnchor = null,
  onCitationClick = null,
  onBeforeSend = null,
  // Optional helpers to prefill the input or prefill+auto-send
  prefillInputText = '',
  onClearPrefillInput = null,
  prefillAndSendText = '',
  onClearPrefillAndSend = null,
  chapterId = null,
  subjectId = null,
  pageContext = 'learn', // learn, curiosity, curiosity_video, curiosity_podcast, curiosity_content, curiosity_news, curiosity_news_detail
  curiosityContentId = null,
  topicId = null,
  newsId = null
  ,
  // Mobile layout props
  isMobileSplit = false,
  isFullScreenJourney = false,
  onRequestFullJourney = null,
  onTogglePdf = null,
  hideStatsBar = false,
}) {
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]);
  const [inputText, setInputText] = useState('');
  const inputRef = useRef(null);
  const [contextText, setContextText] = useState('');
  const [contextAnchor, setContextAnchor] = useState(null);

  // Prevent stale async explanation-mode regeneration from overwriting the UI.
  const explanationModeRequestIdRef = useRef(0);

  // Auto-focus input when component mounts and is visible
  useEffect(() => {
    const focusInput = () => {
      try {
        if (inputRef.current && typeof inputRef.current.focus === 'function') {
          inputRef.current.focus();
        }
      } catch (e) {
        console.warn('Failed to focus chat input:', e);
      }
    };

    // Delay slightly to ensure DOM is fully ready
    const timeoutId = setTimeout(focusInput, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // State for inline media preview
  const [selectedMedia, setSelectedMedia] = useState(null);

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const handleMediaClick = (type, data) => {
    setSelectedMedia({ type, data });
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
  };

  // Explanation level state with localStorage persistence
  const [explanationLevel, setExplanationLevel] = useState(() => {
    try {
      const saved = localStorage.getItem('budlee_explanation_level');
      return saved || 'textbook'; // Default to textbook
    } catch {
      return 'textbook';
    }
  });

  // Persist explanation level to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('budlee_explanation_level', explanationLevel);
    } catch (e) {
      console.warn('Failed to save explanation level preference:', e);
    }
  }, [explanationLevel]);

  const [currentSessionId, setCurrentSessionId] = useState(null);

  useEffect(() => {
    messagesRef.current = messages;

    // Sync to global memory cache to persist tabs across page navigation
    if (currentSessionId && messages.length > 0) {
      globalSessionCache.set(currentSessionId, {
        messages,
        explanationLevel
      });
    }
  }, [messages, currentSessionId, explanationLevel]);
  const [isSending, setIsSending] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const regenerationAbortRef = useRef(null);
  const speakNextReplyRef = useRef(false);
  const [isTtsSpeaking, setIsTtsSpeaking] = useState(false);
  const [isTtsPaused, setIsTtsPaused] = useState(false);
  const pendingAutoSendRef = useRef(null);
  const messageContainerRefById = useRef(new Map());
  const responseAnchorRefById = useRef(new Map());
  const chatScrollContainerRef = useRef(null);
  const isUserNearBottomRef = useRef(true);
  const forceScrollNextRef = useRef(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [scrollToMessageId, setScrollToMessageId] = useState(null);
  const scrollAttemptRef = useRef({ id: null, count: 0 });
  const { updateSelection } = useSubjectChapter();

  const normalizeMediaPayload = (resp) => {
    const media = resp?.media;
    const images = Array.isArray(media?.images) ? media.images : (Array.isArray(resp?.images) ? resp.images : []);
    const videos = Array.isArray(media?.videos) ? media.videos : (Array.isArray(resp?.videos) ? resp.videos : []);
    return { images, videos };
  };

  // Track whether the user is near the bottom of the chat scroll area.
  // If the user scrolls up to read older messages, avoid auto-scrolling new content into view.
  useEffect(() => {
    const el = chatScrollContainerRef.current;
    if (!el) return;

    const thresholdPx = 220;
    const updateNearBottom = () => {
      try {
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        const nearBottom = distanceFromBottom <= thresholdPx;
        isUserNearBottomRef.current = nearBottom;
        if (nearBottom) setShowJumpToLatest(false);
      } catch {
        // ignore
      }
    };

    updateNearBottom();
    el.addEventListener('scroll', updateNearBottom, { passive: true });
    return () => el.removeEventListener('scroll', updateNearBottom);
  }, []);

  const { isSupported: isSpeechToTextSupported, isListening, toggle: toggleSpeechToText, stop: stopSpeechToText } = useSpeechToText({
    lang: 'en-US',
    onFinalTranscript: (finalText) => {
      setInputText((prev) => {
        const trimmed = String(prev || '').trim();
        return trimmed ? `${trimmed} ${finalText}` : finalText;
      });
      try {
        inputRef.current?.focus?.();
      } catch { }
    },
    onError: (err) => {
      const msg = err?.message || 'Speech recognition error';
      toast.error('Voice-to-Text failed', { description: msg });
    },
  });

  const speakBudleeReply = (rawText) => {
    if (typeof window === 'undefined') return;
    if (!('speechSynthesis' in window)) {
      toast.error('Text-to-speech not supported', {
        description: 'Your browser does not support speech synthesis.',
      });
      return;
    }

    const text = String(rawText || '')
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\$\$[\s\S]*?\$\$/g, ' ')
      .replace(/\$[^$]*\$/g, ' ')
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      .replace(/[#>*_~\[\]]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!text) return;

    try {
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.lang = 'en-US';
      utter.onstart = () => {
        setIsTtsSpeaking(true);
        setIsTtsPaused(false);
      };
      utter.onend = () => {
        setIsTtsSpeaking(false);
        setIsTtsPaused(false);
      };
      utter.onerror = () => {
        setIsTtsSpeaking(false);
        setIsTtsPaused(false);
      };
      window.speechSynthesis.speak(utter);
    } catch (e) {
      console.error('Speech synthesis failed:', e);
      toast.error('Failed to speak reply');
      setIsTtsSpeaking(false);
      setIsTtsPaused(false);
    }
  };

  const stopBudleeAudio = () => {
    try {
      window.speechSynthesis?.cancel?.();
    } catch { }
    setIsTtsSpeaking(false);
    setIsTtsPaused(false);
  };

  const pauseBudleeAudio = () => {
    try {
      if (window.speechSynthesis?.speaking && !window.speechSynthesis?.paused) {
        window.speechSynthesis.pause();
        setIsTtsPaused(true);
      }
    } catch { }
  };

  const resumeBudleeAudio = () => {
    try {
      if (window.speechSynthesis?.paused) {
        window.speechSynthesis.resume();
        setIsTtsPaused(false);
      }
    } catch { }
  };

  const sendDirectMessageRef = useRef(null);
  const { isSupported: isVoiceConversationSupported, isListening: isVoiceConversationListening, toggle: toggleVoiceConversation, stop: stopVoiceConversation } = useSpeechToText({
    lang: 'en-US',
    interimResults: false,
    continuous: false,
    onFinalTranscript: async (finalText) => {
      const text = String(finalText || '').trim();
      if (!text) return;
      speakNextReplyRef.current = true;
      try {
        await sendDirectMessageRef.current?.(text);
      } catch (e) {
        console.error('Voice conversation send failed:', e);
        speakNextReplyRef.current = false;
      }
    },
    onError: (err) => {
      const msg = err?.message || 'Speech recognition error';
      toast.error('Voice conversation failed', { description: msg });
      speakNextReplyRef.current = false;
    },
  });

  const handleCloseChat = () => {
    speakNextReplyRef.current = false;
    stopSpeechToText();
    stopVoiceConversation();
    stopBudleeAudio();
    if (typeof onClose === 'function') onClose();
  };

  // Always stop audio if the chat panel unmounts
  useEffect(() => {
    return () => {
      speakNextReplyRef.current = false;
      try {
        stopSpeechToText();
        stopVoiceConversation();
      } catch { }
      stopBudleeAudio();
    };
  }, [stopSpeechToText, stopVoiceConversation]);

  // Handle prefilled text from text selection - show as context box, not in input
  useEffect(() => {
    if (prefilledText) {
      setContextText(coerceToPlainText(prefilledText));
      // Clear prefilled text after setting
      if (onClearPrefilledText) {
        onClearPrefilledText();
      }
      // Focus the input for user interaction
      setTimeout(() => {
        try {
          const ta = inputRef.current;
          if (ta) ta.focus();
        } catch (e) { }
      }, 100);
    }
  }, [prefilledText, onClearPrefilledText]);

  // Optional selection anchor (e.g., from PDF selection) to improve retrieval/citations
  useEffect(() => {
    if (prefilledAnchor) {
      setContextAnchor(prefilledAnchor);
      if (onClearPrefilledAnchor) onClearPrefilledAnchor();
    }
  }, [prefilledAnchor, onClearPrefilledAnchor]);

  // Adjust textarea height whenever inputText changes (handles programmatic prefills)
  useEffect(() => {
    try {
      const ta = inputRef.current;
      if (ta) {
        ta.style.height = 'auto';
        const newHeight = Math.max(32, Math.min(ta.scrollHeight, 200));
        ta.style.height = newHeight + 'px';
      }
    } catch { }
  }, [inputText]);

  // Prefill input (do not auto-send) - used when user clicks "Chat"
  useEffect(() => {
    if (prefillInputText) {
      setInputText(coerceToPlainText(prefillInputText));
      // focus the input for quick editing
      setTimeout(() => {
        try {
          const ta = inputRef.current;
          ta?.focus();
          if (ta) {
            ta.style.height = 'auto';
            const newHeight = Math.max(32, Math.min(ta.scrollHeight, 200));
            ta.style.height = newHeight + 'px';
          }
        } catch (e) { }
      }, 40);
      // Optionally clear the value in parent
      if (onClearPrefillInput) {
        onClearPrefillInput();
      }
    }
  }, [prefillInputText, onClearPrefillInput]);

  // Prefill input and auto-send - used when user clicks "Ask Budlee AI"
  useEffect(() => {
    if (!prefillAndSendText) return;
    // Send directly (no editor prefill) if session ready; otherwise queue
    const sendDirectOrQueue = async (text) => {
      if (!text) return;
      if (currentSessionId) {
        toast.info('Asking Budlee AI...');
        try {
          await sendDirectMessage(text);
        } catch (e) {
          console.error('Error in auto-send direct:', e);
        }
        if (onClearPrefillAndSend) onClearPrefillAndSend();
      } else {
        pendingAutoSendRef.current = text;
      }
    };

    sendDirectOrQueue(coerceToPlainText(prefillAndSendText));
  }, [prefillAndSendText, currentSessionId, onClearPrefillAndSend]);

  // Listen for auto-explain events from Explain button
  useEffect(() => {
    const handleAutoExplain = (event) => {
      const { text } = event.detail || {};
      if (text && currentSessionId) {
        sendDirectMessage(text);
      }
    };

    window.addEventListener('autoSendExplain', handleAutoExplain);
    return () => window.removeEventListener('autoSendExplain', handleAutoExplain);
  }, [currentSessionId]);

  // Helper: send a query directly using current context (no editor prefill)
  const sendDirectMessage = async (questionText) => {
    if (!questionText || isSending) return;
    if (!currentSessionId) {
      toast.error('Chat session not ready. Please wait...');
      return;
    }

    if (onBeforeSend) onBeforeSend();

    const anchorToSend = contextText ? contextAnchor : null;
    const finalQuestion = contextText ? `Context: "${contextText}"\n\nQuestion: ${questionText}` : questionText;

    const messageId = Date.now();
    const userMessage = {
      id: messageId,
      question: finalQuestion,
      answer: '',
      key_points: [],
      suggested_questions: [],
      page_sources: null,
      page_sources_detailed: null,
      source_type: null,
      figure_references: [],
      activeTab: 'all',
      timestamp: new Date(),
      media: { images: [], videos: [] },
      images: [],
      videos: [],
      sources: [],
      status: 'pending'
    };

    setMessages(prev => [...prev, userMessage]);
    forceScrollNextRef.current = true;
    setScrollToMessageId(messageId);
    setIsSending(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const teacherNoteId = sessionStorage.getItem('currentTeacherNoteId');
      const noteId = teacherNoteId ? parseInt(teacherNoteId, 10) : null;
      if (teacherNoteId) sessionStorage.removeItem('currentTeacherNoteId');

      // Streaming (SSE): progressively fill the answer bubble.
      const payload = { content: finalQuestion };
      if (noteId !== null && noteId !== undefined) payload.teacher_note_id = noteId;
      if (anchorToSend) payload.anchor = anchorToSend;
      if (explanationLevel) payload.explanation_mode = explanationLevel;

      let streamedText = '';
      let pending = '';
      let lastFlush = 0;
      const flush = () => {
        if (!pending) return;
        streamedText += pending;
        pending = '';
        setMessages(prev => prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return { ...msg, answer: streamedText };
        }));
      };

      let response = null;
      try {
        response = await sendChatMessageStream(currentSessionId, payload, {
          signal: controller.signal,
          onDelta: (evt) => {
            const text = evt?.text || '';
            if (!text) return;
            pending += text;
            const now = Date.now();
            if (now - lastFlush > 60) {
              lastFlush = now;
              flush();
            }
          },
          onReplace: (evt) => {
            const text = evt?.text || '';
            if (text === '') return;
            streamedText = text;
            pending = '';
            lastFlush = Date.now();
            setMessages(prev => prev.map((msg) => {
              if (!msg || msg.id !== messageId) return msg;
              return { ...msg, answer: streamedText };
            }));
          },
        });
      } catch (e) {
        const cancelled = e?.code === 'CANCELLED' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
        if (cancelled) {
          // Leave message as-is; caller may handle cancel UI.
          return;
        }
        throw e;
      } finally {
        flush();
      }

      if (!response) {
        // Fallback to non-stream.
        response = await sendChatMessage(currentSessionId, finalQuestion, controller.signal, noteId, anchorToSend, explanationLevel);
      }

      const rawAnswer = response.answer ?? response.content ?? '';
      const answerText = coerceToPlainText(rawAnswer);
      const media = normalizeMediaPayload(response);

      setMessages(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        // Cache this answer with the current explanation level
        const answerKey = `answer_${explanationLevel}_v2`;
        const keyPointsKey = `key_points_${explanationLevel}_v2`;
        const suggestedQuestionsKey = `suggested_questions_${explanationLevel}_v2`;
        const pageSourcesKey = `page_sources_${explanationLevel}_v2`;
        const pageSourcesDetailedKey = `page_sources_detailed_${explanationLevel}_v2`;
        const sourceTypeKey = `source_type_${explanationLevel}_v2`;
        const imagesKey = `images_${explanationLevel}_v2`;
        const videosKey = `videos_${explanationLevel}_v2`;
        const sourcesKey = `sources_${explanationLevel}_v2`;
        const figureRefsKey = `figure_references_${explanationLevel}_v2`;
        const mediaKey = `media_${explanationLevel}_v2`;
        return prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return {
            ...msg,
            status: 'done',

            // Cache per-mode
            [answerKey]: answerText,
            [keyPointsKey]: response.key_points || [],
            [suggestedQuestionsKey]: response.suggested_questions || [],
            [pageSourcesKey]: response.page_sources || null,
            [pageSourcesDetailedKey]: response.page_sources_detailed || null,
            [sourceTypeKey]: response.source_type || 'beyond_book',
            [mediaKey]: media,
            [imagesKey]: media.images || [],
            [videosKey]: media.videos || [],
            [sourcesKey]: response.sources || [],
            [figureRefsKey]: response.figure_references || [],

            // Current display
            answer: answerText,
            key_points: response.key_points || [],
            suggested_questions: response.suggested_questions || [],
            page_sources: response.page_sources || null,
            page_sources_detailed: response.page_sources_detailed || null,
            source_type: response.source_type || 'beyond_book',
            media,
            images: media.images || [],
            videos: media.videos || [],
            sources: response.sources || [],
            figure_references: response.figure_references || [],
          };
        });
      });

      if (speakNextReplyRef.current) {
        speakNextReplyRef.current = false;
        speakBudleeReply(answerText);
      }

      if (response.show_curiosity_invite) {
        toast.info('Explore more in Curiosity Centre!', {
          description: 'Check out related videos, podcasts and resources'
        });
      }
    } catch (error) {
      if (isCancelError(error)) {
        setMessages(prev => prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return { ...msg, status: 'done' };
        }));
        return;
      }
      console.error('Chat API error (direct send):', error);
      speakNextReplyRef.current = false;
      setMessages(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        return prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return {
            ...msg,
            status: 'done',
            answer: `Sorry, I encountered an error: ${error.message || 'Failed to get response'}`,
          };
        });
      });
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
      // Clear context after sending
      setContextText('');
      setContextAnchor(null);
      // Focus the input for continued conversation
      setTimeout(() => {
        try {
          const ta = inputRef.current;
          if (ta) ta.focus();
        } catch (e) { }
      }, 100);
    }
  };

  // Keep the ref in sync for voice conversation callbacks
  useEffect(() => {
    sendDirectMessageRef.current = sendDirectMessage;
  }, [sendDirectMessage]);

  // Check for sessionId from navigation state (when resuming from history)
  useEffect(() => {
    const checkForResumedSession = async () => {
      // Check if we're resuming a session from history
      const state = window.history.state;
      if (state?.sessionId) {
        try {
          setSessionLoading(true);
          const session = await getChatSession(state.sessionId);
          setCurrentSessionId(session.session_id);

          // Load messages
          if (session.messages && session.messages.length > 0) {
            const formattedMessages = session.messages.map(msg => {
              const baseContent = msg.role === 'assistant' ? (msg.answer || msg.content) : '';
              const mode = msg.source_type || 'beyond_book';
              const mediaObj = msg.media || { images: msg.images || [], videos: msg.videos || [] };

              const result = {
                id: msg.message_id,
                question: msg.role === 'user' ? msg.content : '',
                answer: baseContent,
                key_points: msg.key_points || [],
                suggested_questions: msg.suggested_questions || [],
                page_sources: msg.page_sources || null,
                page_sources_detailed: msg.page_sources_detailed || null,
                source_type: msg.source_type || 'beyond_book',
                show_curiosity_invite: msg.show_curiosity_invite || false,
                figure_references: msg.figure_references || [],
                activeTab: 'all',
                timestamp: new Date(msg.created_at || msg.timestamp),
                media: mediaObj,
                images: msg.images || [],
                videos: msg.videos || [],
                sources: msg.sources || [],
                status: 'done'
              };

              if (msg.role === 'assistant') {
                result[`answer_${mode}_v2`] = baseContent;
                if (msg.key_points) result[`key_points_${mode}_v2`] = msg.key_points;
                if (msg.suggested_questions) result[`suggested_questions_${mode}_v2`] = msg.suggested_questions;
                if (msg.page_sources) result[`page_sources_${mode}_v2`] = msg.page_sources;
                if (msg.page_sources_detailed) result[`page_sources_detailed_${mode}_v2`] = msg.page_sources_detailed;
                result[`source_type_${mode}_v2`] = msg.source_type;
                if (msg.show_curiosity_invite) result[`show_curiosity_invite_${mode}_v2`] = msg.show_curiosity_invite;
                if (msg.figure_references) result[`figure_references_${mode}_v2`] = msg.figure_references;
                result[`media_${mode}_v2`] = mediaObj;
                if (msg.images) result[`images_${mode}_v2`] = msg.images;
                if (msg.videos) result[`videos_${mode}_v2`] = msg.videos;
                if (msg.sources) result[`sources_${mode}_v2`] = msg.sources;
              }
              return result;
            });

            const cachedState = globalSessionCache.get(session.session_id);
            let finalMessages = formattedMessages;

            if (cachedState && cachedState.messages) {
              finalMessages = formattedMessages.map(apiMsg => {
                const cached = cachedState.messages.find(m => m.id === apiMsg.id);
                if (!cached) return apiMsg;
                const merged = { ...cached };
                Object.keys(apiMsg).forEach(key => {
                  if (apiMsg[key] !== undefined) merged[key] = apiMsg[key];
                });
                return merged;
              });
            }
            setMessages(finalMessages);

            // Align the UI mode with the cached state OR the latest AI message
            if (cachedState && cachedState.explanationLevel) {
              setExplanationLevel(cachedState.explanationLevel);
            } else {
              const latestAiMsg = session.messages.slice().reverse().find(m => m.role === 'assistant');
              if (latestAiMsg && latestAiMsg.source_type) {
                const reverseModeMap = {
                  'textbook': 'textbook',
                  'detailed': 'detailed',
                  'beyond_book': 'beyond_book'
                };
                const modeToRestore = reverseModeMap[latestAiMsg.source_type];
                if (modeToRestore) {
                  setExplanationLevel(modeToRestore);
                }
              }
            }
          }

          // Restore subject/chapter selection from session when available
          try {
            // Only apply session-based selection if parent didn't provide one.
            // Otherwise, this can override Learn's current chapter.
            const hasParentSelection = !!subjectId && !!chapterId;
            if (!hasParentSelection && (session.subject_id || session.chapter_id)) {
              updateSelection(
                session.subject_id || null,
                session.subject_name || '',
                session.chapter_id || null,
                session.chapter_name || ''
              );
            }
          } catch (err) {
            // ignore if context provider not available
          }

          // Clear state to prevent reloading
          window.history.replaceState({}, '');
        } catch (error) {
          console.error('Error loading resumed session:', error);
          toast.error('Failed to load chat session');
        } finally {
          setSessionLoading(false);
        }
        return; // Don't create new session if resuming
      }
    };

    checkForResumedSession();
  }, []);

  // Initialize session when component mounts or context changes
  useEffect(() => {
    const initializeSession = async () => {
      // Skip if we just loaded a resumed session
      const state = window.history.state;
      if (state?.sessionId) {
        return;
      }

      // For learn context, require subject and chapter
      if (pageContext === 'learn' && (!subjectId || !chapterId)) {
        return;
      }

      try {
        setSessionLoading(true);
        const sessionParams = {
          page_context: pageContext,
          subject_id: subjectId || null,
          chapter_id: chapterId || null,
          curiosity_content_id: curiosityContentId || null,
          topic_id: topicId || null,
          news_id: newsId || null
        };

        // getOrCreateSession will find existing session if context matches
        const session = await getOrCreateSession(sessionParams);
        setCurrentSessionId(session.session_id);

        // Load existing messages if session was retrieved
        if (session.messages && session.messages.length > 0) {
          const formattedMessages = session.messages.map(msg => {
            const baseContent = msg.role === 'assistant' ? (msg.answer || msg.content) : '';
            const mode = msg.source_type || 'beyond_book';
            const mediaObj = msg.media || { images: msg.images || [], videos: msg.videos || [] };

            const result = {
              id: msg.message_id,
              question: msg.role === 'user' ? msg.content : '',
              answer: baseContent,
              key_points: msg.key_points || [],
              suggested_questions: msg.suggested_questions || [],
              page_sources: msg.page_sources || null,
              page_sources_detailed: msg.page_sources_detailed || null,
              source_type: msg.source_type || 'beyond_book',
              show_curiosity_invite: msg.show_curiosity_invite || false,
              figure_references: msg.figure_references || [],
              activeTab: 'all',
              timestamp: new Date(msg.created_at || msg.timestamp),
              media: mediaObj,
              images: msg.images || [],
              videos: msg.videos || [],
              sources: msg.sources || [],
              status: 'done'
            };

            if (msg.role === 'assistant') {
              result[`answer_${mode}_v2`] = baseContent;
              if (msg.key_points) result[`key_points_${mode}_v2`] = msg.key_points;
              if (msg.suggested_questions) result[`suggested_questions_${mode}_v2`] = msg.suggested_questions;
              if (msg.page_sources) result[`page_sources_${mode}_v2`] = msg.page_sources;
              if (msg.page_sources_detailed) result[`page_sources_detailed_${mode}_v2`] = msg.page_sources_detailed;
              result[`source_type_${mode}_v2`] = msg.source_type;
              if (msg.show_curiosity_invite) result[`show_curiosity_invite_${mode}_v2`] = msg.show_curiosity_invite;
              if (msg.figure_references) result[`figure_references_${mode}_v2`] = msg.figure_references;
              result[`media_${mode}_v2`] = mediaObj;
              if (msg.images) result[`images_${mode}_v2`] = msg.images;
              if (msg.videos) result[`videos_${mode}_v2`] = msg.videos;
              if (msg.sources) result[`sources_${mode}_v2`] = msg.sources;
            }
            return result;
          });

          const cachedState = globalSessionCache.get(session.session_id);
          let finalMessages = formattedMessages;

          if (cachedState && cachedState.messages) {
            finalMessages = formattedMessages.map(apiMsg => {
              const cached = cachedState.messages.find(m => m.id === apiMsg.id);
              if (!cached) return apiMsg;
              const merged = { ...cached };
              Object.keys(apiMsg).forEach(key => {
                if (apiMsg[key] !== undefined) merged[key] = apiMsg[key];
              });
              return merged;
            });
          }
          setMessages(finalMessages);

          // Align the UI mode with the cached state OR the latest AI message
          if (cachedState && cachedState.explanationLevel) {
            setExplanationLevel(prev => {
              if (prev === 'textbook' || prev === cachedState.explanationLevel) {
                return cachedState.explanationLevel;
              }
              return prev;
            });
          } else {
            const latestAiMsg = session.messages.slice().reverse().find(m => m.role === 'assistant');
            if (latestAiMsg && latestAiMsg.source_type) {
              const reverseModeMap = {
                'textbook': 'textbook',
                'detailed': 'detailed',
                'beyond_book': 'beyond_book'
              };
              const modeToRestore = reverseModeMap[latestAiMsg.source_type];
              if (modeToRestore) {
                setExplanationLevel(prev => {
                  if (prev === 'textbook' || prev === modeToRestore) {
                    return modeToRestore;
                  }
                  return prev;
                });
              }
            }
          }
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Error initializing chat session:', error);
        toast.error('Failed to initialize chat');
      } finally {
        setSessionLoading(false);
        // If there was a pending auto-send, trigger it now
        if (pendingAutoSendRef.current) {
          toast.info('Asking Budlee AI...');
          // Delay to allow explanationLevel state updates & PATCH requests to complete before POST
          setTimeout(() => {
            try {
              sendDirectMessage(pendingAutoSendRef.current);
            } catch (e) {
              console.error('Error sending pending auto-send:', e);
            }
          }, 500);
          pendingAutoSendRef.current = null;
          if (onClearPrefillAndSend) onClearPrefillAndSend();
        }
      }
    };

    initializeSession();
  }, [pageContext, subjectId, chapterId, curiosityContentId, topicId, newsId]);



  // Keep the newest generated response visible from the top (avoid jumping to the bottom
  // which forces users to scroll up to read the beginning of long answers).
  useEffect(() => {
    if (!scrollToMessageId) return;

    const el =
      responseAnchorRefById.current.get(scrollToMessageId) ||
      messageContainerRefById.current.get(scrollToMessageId);

    if (!el) {
      const attempt = scrollAttemptRef.current;
      const nextCount = attempt.id === scrollToMessageId ? attempt.count + 1 : 1;
      scrollAttemptRef.current = { id: scrollToMessageId, count: nextCount };
      if (nextCount <= 6) {
        const id = scrollToMessageId;
        setScrollToMessageId(null);
        setTimeout(() => setScrollToMessageId(id), 60);
      } else {
        setScrollToMessageId(null);
      }
      return;
    }

    scrollAttemptRef.current = { id: null, count: 0 };

    const shouldAutoScroll = forceScrollNextRef.current || isUserNearBottomRef.current;
    if (!shouldAutoScroll) {
      setShowJumpToLatest(true);
      setScrollToMessageId(null);
      return;
    }

    forceScrollNextRef.current = false;
    setShowJumpToLatest(false);
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        try {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        } catch { }
      }, 120);
    } catch {
      // ignore
    } finally {
      setScrollToMessageId(null);
    }
  }, [scrollToMessageId]);

  // Utility to generate structured cache keys for a given mode
  const getModeKeys = (level) => ({
    answerKey: `answer_${level}_v2`,
    keyPointsKey: `key_points_${level}_v2`,
    pageSourcesKey: `page_sources_${level}_v2`,
    pageSourcesDetailedKey: `page_sources_detailed_${level}_v2`,
    sourceTypeKey: `source_type_${level}_v2`,
    mediaKey: `media_${level}_v2`,
    imagesKey: `images_${level}_v2`,
    videosKey: `videos_${level}_v2`,
    sourcesKey: `sources_${level}_v2`,
    suggestedQuestionsKey: `suggested_questions_${level}_v2`,
    figureRefsKey: `figure_references_${level}_v2`,
    showCuriosityInviteKey: `show_curiosity_invite_${level}_v2`,
  });

  // Update session explanation mode when level changes (for new messages)
  const prevExplanationLevelRef = useRef(explanationLevel);
  useEffect(() => {
    if (!currentSessionId || !explanationLevel) {
      prevExplanationLevelRef.current = explanationLevel;
      return;
    }

    // Keep server-side session mode in sync
    updateSessionExplanationMode(currentSessionId, explanationLevel).catch(console.warn);
    prevExplanationLevelRef.current = explanationLevel;
  }, [explanationLevel, currentSessionId]);

  // Per-message toggle handler
  const handleMessageModeToggle = async (messageId, targetMode) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg || msg.display_mode === targetMode || msg.status === 'regenerating') return;

    const keys = getModeKeys(targetMode);

    // 1. Check if we already have it cached locally in React State
    if (msg[keys.answerKey]) {
      setMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          display_mode: targetMode,
          answer: m[keys.answerKey],
          key_points: m[keys.keyPointsKey] || [],
          suggested_questions: m[keys.suggestedQuestionsKey] || m.suggested_questions || [],
          page_sources: m[keys.pageSourcesKey] ?? m.page_sources ?? null,
          page_sources_detailed: m[keys.pageSourcesDetailedKey] ?? m.page_sources_detailed ?? null,
          source_type: m[keys.sourceTypeKey] ?? m.source_type ?? null,
          show_curiosity_invite: m[keys.showCuriosityInviteKey] ?? m.show_curiosity_invite ?? false,
          media: m[keys.mediaKey] ?? m.media ?? { images: m.images || [], videos: m.videos || [] },
          images: m[keys.imagesKey] ?? m.images ?? [],
          videos: m[keys.videosKey] ?? m.videos ?? [],
          sources: m[keys.sourcesKey] ?? m.sources ?? [],
          figure_references: m[keys.figureRefsKey] ?? m.figure_references ?? [],
        };
      }));
      return;
    }

    // 2. Otherwise, ping backend for regeneration
    toast.info(`Generating ${targetMode} explanation...`);
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'regenerating', display_mode: targetMode } : m));
    setIsRegenerating(true);

    // Update backend session transiently so semantic cache filter works correctly for this request
    try {
      await updateSessionExplanationMode(currentSessionId, targetMode);
    } catch (e) {
      console.warn("Failed to ping session mode update, generation may use stale cache.", e);
    }

    try {
      const controller = new AbortController();
      regenerationAbortRef.current = controller;

      let streamedText = '';
      let pending = '';
      let lastFlush = 0;
      const flush = () => {
        if (!pending) return;
        streamedText += pending;
        pending = '';
        setMessages(prev => prev.map((m) => {
          if (m.id !== messageId) return m;
          return { ...m, answer: streamedText };
        }));
      };

      let response = null;
      try {
        response = await sendChatMessageStream(currentSessionId, {
          content: msg.question,
          explanation_mode: targetMode,
        }, {
          signal: controller.signal,
          onDelta: (evt) => {
            const text = evt?.text || '';
            if (!text) return;
            pending += text;
            const now = Date.now();
            if (now - lastFlush > 60) {
              lastFlush = now;
              flush();
            }
          }
          ,
          onReplace: (evt) => {
            const text = evt?.text || '';
            if (text === '') return;
            streamedText = text;
            pending = '';
            lastFlush = Date.now();
            setMessages(prev => prev.map((m) => {
              if (m.id !== messageId) return m;
              return { ...m, answer: streamedText };
            }));
          },
        });
      } finally {
        flush();
      }

      if (!response) {
        response = await sendChatMessage(currentSessionId, msg.question, controller.signal, null, null, targetMode);
      }
      const rawAnswer = response.answer ?? response.content ?? '';
      const answerText = coerceToPlainText(rawAnswer);
      const media = normalizeMediaPayload(response);

      setMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          status: 'done',
          display_mode: targetMode,
          // Cache per-mode response
          [keys.answerKey]: answerText,
          [keys.keyPointsKey]: response.key_points || [],
          [keys.suggestedQuestionsKey]: response.suggested_questions || [],
          [keys.pageSourcesKey]: response.page_sources || null,
          [keys.pageSourcesDetailedKey]: response.page_sources_detailed || null,
          [keys.sourceTypeKey]: response.source_type || targetMode,
          [keys.mediaKey]: media,
          [keys.imagesKey]: media.images || [],
          [keys.videosKey]: media.videos || [],
          [keys.sourcesKey]: response.sources || [],
          [keys.figureRefsKey]: response.figure_references || [],
          [keys.showCuriosityInviteKey]: response.show_curiosity_invite || false,
          // Apply as current display
          answer: answerText,
          key_points: response.key_points || [],
          suggested_questions: response.suggested_questions || [],
          page_sources: response.page_sources || null,
          page_sources_detailed: response.page_sources_detailed || null,
          source_type: response.source_type || targetMode,
          media,
          images: media.images || [],
          videos: media.videos || [],
          sources: response.sources || [],
          figure_references: response.figure_references || [],
          show_curiosity_invite: response.show_curiosity_invite || false,
        };
      }));
      toast.success(`${targetMode.charAt(0).toUpperCase() + targetMode.slice(1)} explanation ready`);

      // Restore global explanation session mode if it's different from what we just regened
      if (explanationLevel !== targetMode) {
        updateSessionExplanationMode(currentSessionId, explanationLevel).catch(console.warn);
      }
    } catch (error) {
      if (isCancelError(error)) {
        console.debug('Regeneration was cancelled');
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'done' } : m));
        if (explanationLevel !== targetMode) {
          updateSessionExplanationMode(currentSessionId, explanationLevel).catch(console.warn);
        }
        return;
      }
      console.error('Failed to generate answer:', error);
      toast.error('Failed to generate answer');
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'done' } : m));

      if (explanationLevel !== targetMode) {
        updateSessionExplanationMode(currentSessionId, explanationLevel).catch(console.warn);
      }
    } finally {
      setIsRegenerating(false);
      regenerationAbortRef.current = null;
    }
  };
  const handleSendMessage = async () => {
    if (isListening) stopSpeechToText();
    if (isVoiceConversationListening) stopVoiceConversation();
    if (!String(inputText || '').trim() || isSending || !currentSessionId) {
      if (!currentSessionId) {
        toast.error('Chat session not ready. Please wait...');
      }
      return;
    }

    if (onBeforeSend) onBeforeSend();

    console.log('BudleeAIChatPanel.handleSendMessage: sending', { inputText, contextText, currentSessionId });

    // Combine context + question seamlessly
    const inputPlain = coerceToPlainText(inputText).trim();
    const contextPlain = coerceToPlainText(contextText).trim();
    let finalQuestion = inputPlain;
    const anchorToSend = contextText ? contextAnchor : null;
    if (contextPlain) {
      finalQuestion = `Context: "${contextPlain}"\n\nQuestion: ${inputPlain}`;
    }

    // Add user message immediately
    const messageId = Date.now();
    const userMessage = {
      id: messageId,
      question: finalQuestion,
      answer: '',
      display_mode: explanationLevel, // Set initial message display mode
      key_points: [],
      suggested_questions: [],
      page_sources: null,
      page_sources_detailed: null,
      source_type: null,
      figure_references: [],
      activeTab: 'all',
      timestamp: new Date(),
      media: { images: [], videos: [] },
      images: [],
      videos: [],
      sources: [],
      status: 'pending'
    };

    setMessages(prev => [...prev, userMessage]);
    forceScrollNextRef.current = true;
    setScrollToMessageId(messageId);
    const query = finalQuestion;
    setInputText('');
    setContextText(''); // Clear context after sending
    setContextAnchor(null);
    setIsSending(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Check for teacherNoteId from sessionStorage (set when Ask BudleeAI is clicked from Class Notes)
      const teacherNoteId = sessionStorage.getItem('currentTeacherNoteId');
      const noteId = teacherNoteId ? parseInt(teacherNoteId, 10) : null;
      // Clear it after use (only use once per message)
      if (teacherNoteId) {
        sessionStorage.removeItem('currentTeacherNoteId');
      }

      // Send message using new API
      // sendChatMessage(sessionId, content, signal?, teacherNoteId?, anchor?)
      let streamedText = '';
      let pending = '';
      let lastFlush = 0;
      const flush = () => {
        if (!pending) return;
        streamedText += pending;
        pending = '';
        setMessages(prev => prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return { ...msg, answer: streamedText };
        }));
      };

      const payload = {
        content: query,
        explanation_mode: explanationLevel,
      };
      if (noteId !== null && noteId !== undefined) payload.teacher_note_id = noteId;
      if (anchorToSend) payload.anchor = anchorToSend;

      let response = null;
      try {
        response = await sendChatMessageStream(currentSessionId, payload, {
          signal: controller.signal,
          onDelta: (evt) => {
            const text = evt?.text || '';
            if (!text) return;
            pending += text;
            const now = Date.now();
            if (now - lastFlush > 60) {
              lastFlush = now;
              flush();
            }
          }
          ,
          onReplace: (evt) => {
            const text = evt?.text || '';
            if (text === '') return;
            streamedText = text;
            pending = '';
            lastFlush = Date.now();
            setMessages(prev => prev.map((msg) => {
              if (!msg || msg.id !== messageId) return msg;
              return { ...msg, answer: streamedText };
            }));
          },
        });
      } finally {
        flush();
      }

      if (!response) {
        response = await sendChatMessage(
          currentSessionId,
          query,
          controller.signal,
          noteId,
          anchorToSend,
          explanationLevel // Explicitly pass the user's selected mode
        );
      }

      const rawAnswer = response.answer ?? response.content ?? '';
      const answerText = coerceToPlainText(rawAnswer);
      const media = normalizeMediaPayload(response);

      // Update the last message with AI response
      setMessages(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;

        // Use the backend's actual source_type for caching (source of truth)
        // This prevents "cache poisoning" where a textbook answer clobbers a detailed key
        const responseMode = response.source_type || explanationLevel;

        const keys = getModeKeys(responseMode);

        return prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return {
            ...msg,
            status: 'done',
            display_mode: responseMode,

            // Cache per-mode (backend as truth)
            [keys.answerKey]: answerText,
            [keys.keyPointsKey]: response.key_points || [],
            [keys.suggestedQuestionsKey]: response.suggested_questions || [],
            [keys.pageSourcesKey]: response.page_sources || null,
            [keys.pageSourcesDetailedKey]: response.page_sources_detailed || null,
            [keys.sourceTypeKey]: response.source_type || responseMode,
            [keys.mediaKey]: media,
            [keys.imagesKey]: media.images || [],
            [keys.videosKey]: media.videos || [],
            [keys.sourcesKey]: response.sources || [],
            [keys.figureRefsKey]: response.figure_references || [],
            [keys.showCuriosityInviteKey]: response.show_curiosity_invite || false,

            // Current display
            answer: answerText,
            key_points: response.key_points || [],
            suggested_questions: response.suggested_questions || [],
            page_sources: response.page_sources || null,
            page_sources_detailed: response.page_sources_detailed || null,
            source_type: response.source_type || responseMode,
            media,
            images: media.images || [],
            videos: media.videos || [],
            sources: response.sources || [],
            figure_references: response.figure_references || [],
            show_curiosity_invite: response.show_curiosity_invite || false,
          };
        });
      });

      if (speakNextReplyRef.current) {
        speakNextReplyRef.current = false;
        speakBudleeReply(answerText);
      }

      // Show curiosity invite if flag is set (legacy toast, now we also have the button)
      if (response.show_curiosity_invite) {
        // Optional: keep toast or remove it if button is enough. Keeping for now as extra nudge.
        toast.info('Explore more in Curiosity Centre!', {
          description: 'Check out related videos, podcasts, and resources'
        });
      }
    } catch (error) {
      if (isCancelError(error)) {
        console.debug('Send was cancelled');
        setMessages(prev => prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return { ...msg, status: 'done' };
        }));
        return;
      }
      console.error('Chat API error:', error);
      speakNextReplyRef.current = false;
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to get response';

      // Update last message with error
      setMessages(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        return prev.map((msg) => {
          if (!msg || msg.id !== messageId) return msg;
          return {
            ...msg,
            status: 'done',
            answer: `Sorry, I encountered an error: ${errorMessage}`,
          };
        });
      });

      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
    }
  };

  const handleStopGeneration = () => {
    try {
      abortControllerRef.current?.abort?.();
    } catch { }
    abortControllerRef.current = null;

    try {
      regenerationAbortRef.current?.abort?.();
    } catch { }
    regenerationAbortRef.current = null;

    setIsSending(false);
    setIsRegenerating(false);
    setMessages(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;

      // If we cancelled a brand-new send before any text arrived,
      // drop that pending message to avoid an empty unanswered bubble.
      const lastPendingIdx = (() => {
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          const m = prev[i];
          if (m && m.status === 'pending') return i;
        }
        return -1;
      })();

      let next = prev;
      if (lastPendingIdx >= 0) {
        const pendingMsg = prev[lastPendingIdx];
        const answerText = String(pendingMsg?.answer || '').trim();
        if (!answerText) {
          next = prev.filter((_, idx) => idx !== lastPendingIdx);
        } else {
          next = prev.map((m, idx) => {
            if (idx !== lastPendingIdx) return m;
            return { ...m, status: 'done' };
          });
        }
      }

      // Revert any regenerating messages to done
      return next.map(m => {
        if (!m) return m;
        if (m.status === 'regenerating') return { ...m, status: 'done' };
        return m;
      });
    });

    toast.info('Generation stopped');
    if (currentSessionId) {
      cancelChatGeneration(currentSessionId).catch(e => console.error('Cancel failed', e));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleVoiceToText = () => {
    if (!isSpeechToTextSupported) {
      toast.error('Voice-to-Text not supported', {
        description: 'Try Chrome/Edge on HTTPS (or localhost).',
      });
      return;
    }

    toggleSpeechToText();
  };

  const handleTalkToBudleeAI = () => {
    if (!isVoiceConversationSupported) {
      toast.error('Voice conversation not supported', {
        description: 'Try Chrome/Edge on HTTPS (or localhost).',
      });
      return;
    }

    // Avoid multiple recognizers at the same time.
    if (isListening) stopSpeechToText();

    // If stopping, also stop any ongoing TTS.
    if (isVoiceConversationListening) {
      speakNextReplyRef.current = false;
      try {
        window.speechSynthesis?.cancel?.();
      } catch { }
    }

    toggleVoiceConversation();
  };

  const handleTabChange = (messageId, tab) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, activeTab: tab } : msg
    ));
  };

  const handleCloseContext = () => {
    setContextText('');
  };

  // Add tabIndex to allow focus recovery
  return (
    <div
      tabIndex={0}
      className={`budlee-chat-panel w-full h-full ${isFullScreenJourney ? 'fixed inset-0 z-50 md:static' : ''} bg-[hsl(var(--sidebar-bg))] border-l border-[hsl(var(--card-border))] flex flex-col`}
    >
      {/* Stats Bar - Conditionally Visible */}
      {!hideStatsBar && (
        <div className="p-6 pb-4">
          <StatsBar totalXP={totalXP} compact={true} />
        </div>
      )}

      {/* Close Button - Icon only on mobile, text on desktop */}
      <div className="px-4 md:px-6 pb-2 flex justify-end lg:justify-start">
        <Button
          onClick={handleCloseChat}
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] lg:w-full lg:justify-start"
        >
          <X className="w-4 h-4 lg:mr-2" />
          <span className="hidden lg:inline">Close Chat</span>
        </Button>
      </div>

      {/* Chat Messages Area */}
      <div ref={chatScrollContainerRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {showJumpToLatest && messages.length > 0 && (
          <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none">
            <Button
              type="button"
              size="sm"
              className="pointer-events-auto bg-[hsl(var(--primary))] text-white shadow-lg hover:bg-[hsl(var(--primary))]/90"
              onClick={() => {
                const last = Array.isArray(messages) && messages.length ? messages[messages.length - 1] : null;
                if (!last?.id) return;
                forceScrollNextRef.current = true;
                setScrollToMessageId(last.id);
                setShowJumpToLatest(false);
              }}
            >
              Jump to latest
            </Button>
          </div>
        )}
        {messages.length === 0 ? (
          /* Empty State - "Talk to Budlee" */
          <div className="flex flex-col items-center justify-center h-full">
            <div className="flex flex-col items-center justify-center text-center space-y-4 w-full">
              <div className="flex justify-center">
                <BudleeAIIcon
                  size="w-12 h-12 md:w-16 md:h-16"
                  iconSize="w-5 h-5 md:w-6 md:h-6"
                  position="relative"
                  showBadge={false}
                  className="animate-float"
                />
              </div>
              <h3 className="text-xl md:text-2xl font-bold budlee-brand-logo text-center w-full block">
                Talk to Budlee
              </h3>
              <p className="text-muted-foreground text-sm text-center">
                Your AI Learning Companion
              </p>
            </div>
          </div>
        ) : (
          /* Chat Messages - New Format */
          <>
            {(messages || []).map((conversation) => {
              const figureRefs = Array.isArray(conversation.figure_references) ? conversation.figure_references : [];
              const figureRefMap = new Map(
                figureRefs
                  .map((ref) => {
                    const label = ref?.reference || ref?.figure || '';
                    const pageNum = ref?.page;
                    if (!label || !pageNum) return null;
                    return [normalizeFigureRef(label), pageNum];
                  })
                  .filter(Boolean)
              );

              // Parse context and question from combined format
              const hasContext = conversation.question.includes('Context:');
              let displayContext = '';
              let displayQuestion = conversation.question;

              if (hasContext) {
                const parts = conversation.question.split('\n\nQuestion:');
                if (parts.length >= 2) {
                  const rawContext = parts[0] || '';
                  displayQuestion = parts.slice(1).join('\n\nQuestion:').trim();
                  displayContext = rawContext
                    .replace(/^Context:\s*"?/, '')
                    .replace(/"?\s*$/, '')
                    .trim();
                }
              }

              const media = conversation.media || { images: conversation.images || [], videos: conversation.videos || [] };
              const images = Array.isArray(media.images) ? media.images : (Array.isArray(conversation.images) ? conversation.images : []);
              const videos = Array.isArray(media.videos) ? media.videos : (Array.isArray(conversation.videos) ? conversation.videos : []);
              const detailedSources = Array.isArray(conversation?.page_sources_detailed?.sources)
                ? conversation.page_sources_detailed.sources
                : [];
              const hasImages = images.length > 0;
              const hasVideos = videos.length > 0;
              const hasSources = pageContext !== 'learn' && detailedSources.some(s => typeof s === 'string' && s.trim().length > 0);
              const showTabs = hasImages || hasVideos || hasSources;
              const allowedTabs = new Set([
                ...(showTabs ? ['all'] : []),
                ...(hasImages ? ['images'] : []),
                ...(hasVideos ? ['videos'] : []),
                ...(hasSources ? ['sources'] : []),
              ]);
              const activeTab = allowedTabs.has(conversation.activeTab) ? conversation.activeTab : 'all';

              return (
                <div
                  key={conversation.id}
                  ref={(node) => {
                    if (node) messageContainerRefById.current.set(conversation.id, node);
                    else messageContainerRefById.current.delete(conversation.id);
                  }}
                  className="space-y-4 mb-6 pb-6 border-b border-[hsl(var(--card-border))]"
                >
                  {/* Question - Left Aligned */}
                  <div className="bg-gradient-to-br from-[hsl(var(--card-bg))] to-[hsl(var(--card-bg))]/80 border border-[hsl(var(--card-border))] rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                    {hasContext && displayContext && !displayQuestion.startsWith('Explain:') && (
                      <div className="mb-4 rounded-xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/5 to-transparent backdrop-blur-sm p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-xs font-bold text-[hsl(var(--primary))] mb-2.5">
                          <div className="p-1 rounded-md bg-[hsl(var(--primary))]/10">
                            <Quote className="w-3.5 h-3.5" />
                          </div>
                          <span className="uppercase tracking-wide">Selected Context</span>
                        </div>
                        <p className="text-sm text-foreground/90 leading-relaxed line-clamp-3 font-medium">
                          {coerceToPlainText(displayContext)}
                        </p>
                      </div>
                    )}
                    <p className="text-foreground font-bold text-base leading-relaxed mb-2">
                      {coerceToPlainText(displayQuestion)}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground px-2 py-1 rounded-md bg-[hsl(var(--sidebar-hover))]/30">
                        {conversation.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    {/* Show loading indicator inline when pending */}
                    {conversation.status === 'pending' && (
                      <div className="mt-4 pt-4 border-t border-[hsl(var(--card-border))]/50">
                        {/* Anchor used for scrolling to the response-generation point (loader). */}
                        <div
                          ref={(node) => {
                            if (node) responseAnchorRefById.current.set(conversation.id, node);
                            else responseAnchorRefById.current.delete(conversation.id);
                          }}
                          className="h-px w-full"
                          style={{ scrollMarginTop: 12, scrollMarginBottom: 180 }}
                        />
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce" />
                            <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-100" />
                            <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-200" />
                          </div>
                          <span className="text-sm text-muted-foreground font-medium">Generating response...</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Filter Tabs (only if there is something to show) */}
                  {showTabs && conversation.status !== 'regenerating' && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={activeTab === 'all' ? 'default' : 'outline'}
                        onClick={() => handleTabChange(conversation.id, 'all')}
                        className={activeTab === 'all'
                          ? 'bg-[hsl(var(--primary))] text-white'
                          : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                        }
                      >
                        All
                      </Button>
                      {hasImages && (
                        <Button
                          size="sm"
                          variant={activeTab === 'images' ? 'default' : 'outline'}
                          onClick={() => handleTabChange(conversation.id, 'images')}
                          className={activeTab === 'images'
                            ? 'bg-[hsl(var(--primary))] text-white'
                            : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                          }
                        >
                          Images
                        </Button>
                      )}
                      {hasVideos && (
                        <Button
                          size="sm"
                          variant={activeTab === 'videos' ? 'default' : 'outline'}
                          onClick={() => handleTabChange(conversation.id, 'videos')}
                          className={activeTab === 'videos'
                            ? 'bg-[hsl(var(--primary))] text-white'
                            : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                          }
                        >
                          Videos
                        </Button>
                      )}
                      {hasSources && (
                        <Button
                          size="sm"
                          variant={activeTab === 'sources' ? 'default' : 'outline'}
                          onClick={() => handleTabChange(conversation.id, 'sources')}
                          className={activeTab === 'sources'
                            ? 'bg-[hsl(var(--primary))] text-white'
                            : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))]'
                          }
                        >
                          Sources
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Combined Media Section (Images & Videos) */}
                  {conversation.status !== 'regenerating' && (activeTab === 'all' || activeTab === 'images' || activeTab === 'videos') && (
                    (() => {
                      const mediaItems = [];

                      if (activeTab === 'all') {
                        images.forEach(img => mediaItems.push({ type: 'image', data: img }));
                        videos.forEach(vid => mediaItems.push({ type: 'video', data: vid }));
                      } else if (activeTab === 'images') {
                        images.forEach(img => mediaItems.push({ type: 'image', data: img }));
                      } else if (activeTab === 'videos') {
                        videos.forEach(vid => mediaItems.push({ type: 'video', data: vid }));
                      }

                      if (mediaItems.length === 0) return null;

                      return (
                        <div className="overflow-x-auto pb-2">
                          <div className="flex gap-3">
                            {mediaItems.map((item, idx) => {
                              if (item.type === 'image') {
                                const imgObj = item.data;
                                const src = typeof imgObj === 'string' ? imgObj : (imgObj?.file_url || '');
                                if (!src) return null;
                                return (
                                  <img
                                    key={`img-${idx}`}
                                    src={src}
                                    alt={`Result ${idx + 1}`}
                                    className="w-32 h-24 object-cover rounded-lg border border-[hsl(var(--card-border))] flex-shrink-0 hover:scale-105 transition-transform cursor-pointer"
                                    onClick={() => handleMediaClick('image', { file_url: src })}
                                  />
                                );
                              } else {
                                const video = item.data;
                                const isYoutube = !!video.youtube_url || (video.file_url && video.file_url.includes('youtube.com'));
                                let videoId = null;
                                if (isYoutube) {
                                  try {
                                    const urlObj = new URL(video.youtube_url || video.file_url);
                                    videoId = urlObj.searchParams.get('v');
                                  } catch (e) { }
                                }
                                const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/0.jpg` : null;
                                const videoTitle = video.title || video.file_name || 'Video';

                                return (
                                  <div
                                    key={`vid-${idx}`}
                                    className="relative w-40 h-24 flex-shrink-0 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg overflow-hidden hover:border-[hsl(var(--primary))] cursor-pointer transition-colors group"
                                    title={videoTitle}
                                    onClick={() => handleMediaClick('video', video)}
                                  >
                                    {thumbnailUrl ? (
                                      <img src={thumbnailUrl} alt="Video thumbnail" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                    ) : (
                                      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 p-2 flex items-center justify-center">
                                        <p className="text-[10px] text-white/90 font-medium line-clamp-2 leading-tight text-center">
                                          {video.section_name || videoTitle}
                                        </p>
                                      </div>
                                    )}

                                    {/* Play Button Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className="w-8 h-8 md:w-10 md:h-10 bg-red-600/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                        <span className="text-white text-sm ml-0.5">▶</span>
                                      </div>
                                    </div>

                                    {/* Youtube Badge */}
                                    {isYoutube && (
                                      <div className="absolute bottom-1 right-1">
                                        <span className="text-[8px] bg-black/80 text-white px-1.5 py-0.5 rounded font-bold tracking-wider">YT</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            })}
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {/* Sources Section (placeholder - intentionally empty for now)
                      Still only shown when sources exist (via tab visibility rules). */}
                  {activeTab === 'sources' && detailedSources.length > 0 && conversation.status !== 'regenerating' && (
                    <div className="min-h-[1px]" />
                  )}

                  {/* Regenerating Loader */}
                  {conversation.status === 'regenerating' && (
                    <div className="flex items-center justify-center gap-3 py-4 px-4 rounded-lg bg-gradient-to-r from-[hsl(var(--primary))]/10 to-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/20 shadow-sm mt-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 bg-[hsl(var(--primary))] rounded-full animate-bounce" />
                        <div className="w-2.5 h-2.5 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-100" />
                        <div className="w-2.5 h-2.5 bg-[hsl(var(--primary))] rounded-full animate-bounce delay-200" />
                      </div>
                      <span className="text-sm font-semibold text-[hsl(var(--primary))]">
                        Generating {conversation.display_mode === 'detailed' ? 'Detailed Explanation' : conversation.display_mode === 'beyond_book' ? 'Beyond the Book' : 'Textbook Explanation'}...
                      </span>
                    </div>
                  )}

                  {conversation.answer && conversation.status !== 'regenerating' && (
                    /* Answer Section - Show in All tab (directly after images) */
                    <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-4 space-y-3">
                      {/* Anchor used for scrolling to the response-generation point (answer start). */}
                      <div
                        ref={(node) => {
                          if (node) responseAnchorRefById.current.set(conversation.id, node);
                          else responseAnchorRefById.current.delete(conversation.id);
                        }}
                        className="h-px w-full"
                        style={{ scrollMarginTop: 12, scrollMarginBottom: 180 }}
                      />
                      {conversation.source_type && pageContext !== 'learn' && (
                        <span className="text-xs text-muted-foreground capitalize mb-2 block">
                          Source: {conversation.source_type.replace('_', ' ')}
                        </span>
                      )}

                      {(() => {
                        const sanitizedFullMessage = formatAnswerForDisplay(conversation.answer, { streaming: conversation.status === 'pending' });
                        const { cleanContent, quizData, simulation } = parseInteractiveContent(sanitizedFullMessage);
                        // We previously stripped mermaid blocks here if a simulation existed.
                        // Removing that restriction so both can render.
                        return (
                          <>
                            <CitedAnswer
                              answer={cleanContent}
                              messageId={`${conversation.id}-answer`}
                              figureRefMap={figureRefMap}
                              onCitationClick={onCitationClick}
                              streaming={conversation.status === 'pending'}
                            />
                            {simulation && (
                              <div className="mt-4">
                                {simulation.title && (
                                  <div className="mb-2">
                                    <div className="text-sm font-semibold text-foreground">{simulation.title}</div>
                                    {simulation.description && (
                                      <div className="text-xs text-muted-foreground">{simulation.description}</div>
                                    )}
                                  </div>
                                )}
                                <SimulationRenderer
                                  simulationType={simulation.simulationType}
                                  simulationData={simulation.simulationData}
                                  onComplete={() => { }}
                                />
                              </div>
                            )}
                            {quizData && (
                              <div className="mt-4">
                                <InteractiveQuiz data={quizData} />
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Referenced Figures & Tables (strict, server-provided) */}
                      {figureRefs.length > 0 && typeof onCitationClick === 'function' && (
                        <div className="mt-3 pt-3 border-t border-[hsl(var(--card-border))]">
                          <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-2.5 flex items-center gap-1.5">
                            <span>Referenced Figures & Tables:</span>
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {figureRefs.slice(0, 10).map((ref, idx) => (
                              <button
                                key={`${conversation.id}-figref-${idx}`}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const pageNum = Math.max(1, parseInt(ref.page, 10) || 1);
                                  onCitationClick({ page: pageNum, figure: ref.reference || ref.figure || null });
                                }}
                                className="group px-3 py-2 rounded-lg border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/10 to-[hsl(var(--primary))]/5 hover:from-[hsl(var(--primary))]/20 hover:to-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] text-xs font-medium transition-all duration-300 cursor-pointer flex items-center gap-2 shadow-sm hover:shadow-md hover:scale-[1.02]"
                                title={`${ref.reference || ref.figure || 'Reference'} - Page ${ref.page}${ref.caption ? `: ${ref.caption}` : ''}`}
                              >
                                <span className="font-semibold text-sm">{ref.reference || ref.figure}</span>
                                <span className="text-[hsl(var(--primary))]/50 group-hover:text-[hsl(var(--primary))]/70 transition-colors">•</span>
                                <span className="text-[hsl(var(--primary))]/70 group-hover:text-[hsl(var(--primary))] transition-colors text-xs">Pg {ref.page}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Key Points */}
                      {conversation.key_points && conversation.key_points.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[hsl(var(--card-border))]">
                          <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-2">Key Points:</p>
                          <AnswerMarkdown
                            messageId={`${conversation.id}-keypoints`}
                            className="text-xs text-muted-foreground [&_p]:my-1 [&_li]:my-0"
                            figureRefMap={figureRefMap}
                          >
                            {formatAnswerForDisplay(
                              (conversation.key_points || [])
                                .map((p) => String(p || '').trim().replace(/^[-*+•]\s+/, ''))
                                .filter(Boolean)
                                .map((p) => `- ${p}`)
                                .join('\n')
                            )}
                          </AnswerMarkdown>
                        </div>
                      )}

                      {/* Deprecated single inline switch button removed in favor of segmented top toggle */}

                      {/* Curiosity Invite Button */}
                      {conversation.show_curiosity_invite && (
                        <div className="mt-4 pt-3 border-t border-[hsl(var(--card-border))]">
                          {conversation.curiosity_content_status === 'draft' ? (
                            <div className="p-3 bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500/20 rounded-lg text-center">
                              <Sparkles className="w-5 h-5 mx-auto text-purple-400 mb-2" />
                              <p className="text-sm font-medium text-purple-200">
                                Content is being added. Will be available soon...
                              </p>
                              <p className="text-[10px] text-purple-300 mt-1 italic">
                                We appreciate your curiosity! Keep learning!
                              </p>
                            </div>
                          ) : (
                            <Button
                              onClick={() => {
                                // Navigate to Curiosity Centre
                                if (typeof window !== 'undefined') {
                                  if (conversation.curiosity_content_id) {
                                    window.location.href = `/student/curiosity/content/${conversation.curiosity_content_id}`;
                                  } else {
                                    window.location.href = '/student/curiosity';
                                  }
                                }
                              }}
                              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md transition-all duration-300 transform hover:scale-[1.02]"
                            >
                              <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                              Go to Curiosity Centre
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Switch to Detailed Explanation Explicit Trigger Button */}
                      {pageContext === 'learn' && conversation.status !== 'regenerating' && (() => {
                        const isTextbookCurrent = (conversation.display_mode || conversation.source_type) === 'textbook';
                        const keys = getModeKeys('detailed');
                        const hasDetailedLocally = !!conversation[keys.answerKey] || conversation.source_type === 'detailed';

                        if (isTextbookCurrent && !hasDetailedLocally) {
                          return (
                            <div className="mt-4 pt-3 border-t border-[hsl(var(--card-border))]">
                              <Button
                                onClick={() => handleMessageModeToggle(conversation.id, 'detailed')}
                                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-md transition-all duration-300 transform hover:scale-[1.02]"
                              >
                                <Layers className="w-4 h-4 mr-2" />
                                Switch to Detailed Explanation
                              </Button>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Mode Navigation Pagination */}
                      {pageContext === 'learn' && conversation.status !== 'regenerating' && (
                        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-[hsl(var(--card-border))] text-xs text-muted-foreground w-full">
                          {(() => {
                            const ALL_MODES = ['textbook', 'detailed', 'beyond_book'];
                            const displayLabels = { textbook: 'Textbook Content', detailed: 'Detailed Content', beyond_book: 'Beyond the Book' };

                            // Only include modes that are actively loaded in local memory or natively returned
                            const availableModes = ALL_MODES.filter(mode => {
                              if (conversation.source_type === mode) return true;
                              const keys = getModeKeys(mode);
                              return !!conversation[keys.answerKey];
                            });

                            const currentMode = conversation.display_mode || conversation.source_type || 'textbook';
                            if (!availableModes.includes(currentMode)) {
                              availableModes.push(currentMode);
                              availableModes.sort((a, b) => ALL_MODES.indexOf(a) - ALL_MODES.indexOf(b));
                            }

                            // If only 1 mode is cached, don't show pagination arrows at all
                            if (availableModes.length < 2) return null;

                            const currentIndex = availableModes.indexOf(currentMode);
                            if (currentIndex === -1) return null;

                            return (
                              <>
                                <button
                                  onClick={() => handleMessageModeToggle(conversation.id, availableModes[currentIndex - 1])}
                                  disabled={currentIndex === 0}
                                  className="p-1.5 hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] rounded-md transition-colors disabled:opacity-30"
                                  title="Previous Explanation Level"
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </button>
                                <span className="font-semibold text-[10px] uppercase tracking-wider text-center select-none min-w-[120px]">
                                  {displayLabels[currentMode]}
                                </span>
                                <button
                                  onClick={() => handleMessageModeToggle(conversation.id, availableModes[currentIndex + 1])}
                                  disabled={currentIndex === availableModes.length - 1}
                                  className="p-1.5 hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] rounded-md transition-colors disabled:opacity-30"
                                  title="Next Explanation Level"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 md:p-6 pt-2 space-y-3 border-t border-[hsl(var(--card-border))]">
        {/* Explanation Level Toggle - Only show on Learn page */}
        {pageContext === 'learn' && (
          <div className="bg-[hsl(var(--card-bg))] p-1 rounded-xl border border-[hsl(var(--card-border))] flex relative">
            <div
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg bg-[hsl(var(--primary))] shadow-sm transition-all duration-300 ease-out ${explanationLevel === 'detailed' ? 'translate-x-[calc(100%+8px)]' : 'translate-x-0'
                }`}
            />
            <button
              onClick={() => {
                setExplanationLevel('textbook');
                // RETROACTIVE TOGGLE: If latest message is AI and mode differs, trigger regeneration
                const latestMsg = messages.length > 0 ? messages[messages.length - 1] : null;
                if (latestMsg && latestMsg.answer && latestMsg.source_type !== 'textbook') {
                  handleMessageModeToggle(latestMsg.id, 'textbook');
                }
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium relative z-10 transition-colors duration-300 ${explanationLevel === 'textbook' ? 'text-white' : 'text-muted-foreground hover:text-white'
                }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Textbook</span>
            </button>
            <button
              onClick={() => {
                setExplanationLevel('detailed');
                // RETROACTIVE TOGGLE: If latest message is AI and mode differs, trigger regeneration
                const latestMsg = messages.length > 0 ? messages[messages.length - 1] : null;
                if (latestMsg && latestMsg.answer && latestMsg.source_type !== 'detailed') {
                  handleMessageModeToggle(latestMsg.id, 'detailed');
                }
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium relative z-10 transition-colors duration-300 ${explanationLevel === 'detailed' ? 'text-white' : 'text-muted-foreground hover:text-white'
                }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Detailed</span>
            </button>
          </div>
        )}

        {/* Context Box - Shows selected text */}
        {contextText && (
          <div className="bg-[hsl(var(--card-bg))] border-2 border-[hsl(var(--primary))]/30 rounded-lg p-3 relative">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] mt-1 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-bold text-[hsl(var(--primary))] mb-1">Selected context:</p>
                <p className="text-sm text-gray-300 leading-relaxed line-clamp-2">
                  "{coerceToPlainText(contextText)}"
                </p>
              </div>
              <Button
                onClick={handleCloseContext}
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Text Input + Send */}
        <div className="flex items-center gap-2">
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={(e) => {
              setInputText(String(e.target.value || ''));
              try {
                const ta = inputRef.current;
                if (ta) {
                  ta.style.height = 'auto';
                  ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
                }
              } catch { }
            }}
            onPaste={(e) => {
              // Prevent default paste and manually extract text to avoid [object Object]
              e.preventDefault();
              const cd = e.clipboardData;
              const types = Array.isArray(cd?.types) ? cd.types : [];
              const textPlain = cd?.getData('text/plain') || '';
              const textText = cd?.getData('text') || '';
              const textHtml = cd?.getData('text/html') || '';
              const text = textPlain || textText || textHtml || '';
              const currentValue = String(inputText || '');
              const textarea = e.target;
              const start = textarea.selectionStart || 0;
              const end = textarea.selectionEnd || 0;
              const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
              setInputText(newValue);
              // Set cursor position after pasted text
              setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + text.length;
              }, 0);
            }}
            onKeyDown={handleKeyPress}
            placeholder="Ask Anything..."
            className="flex-1 min-h-[32px] max-h-[200px] bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] resize-none"
            rows={1}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!String(inputText || '').trim() || isSending || !currentSessionId}
            size="icon"
            className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white"
          >
            {isSending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>

          {(isSending || isRegenerating) && (
            <Button
              type="button"
              onClick={handleStopGeneration}
              size="icon"
              variant="outline"
              className="border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
              title="Stop generation"
              aria-label="Stop generation"
            >
              <Square className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Voice Buttons */}
        <div className="grid grid-cols-[auto,minmax(0,1fr),auto,auto] items-center gap-2 overflow-hidden">
          <Button
            onClick={handleVoiceToText}
            size="icon"
            variant="outline"
            className={`col-start-1 ${isListening
              ? 'bg-red-600 border-red-600 text-white animate-pulse'
              : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))]'
              }`}
          >
            <Mic className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleTalkToBudleeAI}
            variant="outline"
            className={`col-start-2 ${isVoiceConversationListening
              ? 'w-full min-w-0 relative z-0 overflow-hidden bg-[hsl(var(--card-bg))] border-[hsl(var(--destructive))] text-foreground animate-destructive-glow'
              : 'w-full min-w-0 relative z-0 overflow-hidden bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))]'
              }`}
          >
            <span className="budlee-brand-logo relative z-10 block truncate">Talk to Budlee</span>
          </Button>

          {/* Audio controls (TTS) - placed next to Talk to Budlee */}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={isTtsPaused ? resumeBudleeAudio : pauseBudleeAudio}
            disabled={!isTtsSpeaking && !isTtsPaused}
            className={`col-start-3 ${(isTtsSpeaking || isTtsPaused)
              ? 'flex-shrink-0 relative z-10 bg-[hsl(var(--card-bg))] border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--sidebar-hover))]'
              : 'flex-shrink-0 relative z-10 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))]'
              }`}
            title={isTtsPaused ? 'Continue audio' : 'Pause audio'}
            aria-label={isTtsPaused ? 'Continue audio' : 'Pause audio'}
          >
            {isTtsPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={stopBudleeAudio}
            disabled={!isTtsSpeaking && !isTtsPaused}
            className={`col-start-4 ${(isTtsSpeaking || isTtsPaused)
              ? 'flex-shrink-0 relative z-10 bg-[hsl(var(--card-bg))] border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--sidebar-hover))]'
              : 'flex-shrink-0 relative z-10 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))]'
              }`}
            title="Stop audio"
            aria-label="Stop audio"
          >
            <Square className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Inline Media Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={closeMediaModal}>
          <div className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden shadow-2xl border border-[hsl(var(--card-border))]" onClick={e => e.stopPropagation()}>
            <button
              onClick={closeMediaModal}
              className="absolute top-2 right-2 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </button>

            <div className="flex items-center justify-center bg-black min-h-[300px] max-h-[80vh]">
              {selectedMedia.type === 'video' ? (
                (() => {
                  const video = selectedMedia.data;
                  const isYoutube = !!video.youtube_url || (video.file_url && video.file_url.includes('youtube.com'));
                  const youtubeId = isYoutube ? getYouTubeVideoId(video.youtube_url || video.file_url) : null;

                  if (isYoutube && youtubeId) {
                    return (
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                        className="w-full aspect-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Video Player"
                      />
                    );
                  } else if (video.file_url) {
                    return (
                      <video
                        src={video.file_url}
                        controls
                        autoPlay
                        className="max-w-full max-h-[80vh]"
                      />
                    );
                  }
                  return <div className="text-white">Video source not found</div>;
                })()
              ) : (
                <img
                  src={typeof selectedMedia.data === 'string' ? selectedMedia.data : selectedMedia.data.file_url}
                  alt="Preview"
                  className="max-w-full max-h-[80vh] object-contain"
                />
              )}
            </div>

            {/* Caption/Details */}
            {(selectedMedia.data.section_name || selectedMedia.data.title || selectedMedia.data.file_name || selectedMedia.data.description) && (
              <div className="p-4 bg-[hsl(var(--card-bg))] border-t border-[hsl(var(--card-border))]">
                <h3 className="text-white font-medium text-base md:text-lg">
                  {selectedMedia.data.section_name || selectedMedia.data.title || selectedMedia.data.file_name}
                </h3>
                {selectedMedia.data.description && (
                  <p className="text-gray-400 text-sm mt-1">{selectedMedia.data.description}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
